import React from "react";
import { Typography, Box, Divider } from "@material-ui/core";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl";
import ActionDialog from "./ActionDialog";
import { showErrorMessage, showSuccessMessage, validateContract } from "../../actions";
import InfoItem from "../../custom/InfoItem";
import Number from "../../custom/Number";
import { resolveErrorMessage } from "../../util";
import LoadingSpinner from "./LoadingSpinner";
import { useDispatch } from "react-redux";

const DialogContractValidation = ({ contract, currentUser, loadContract, open, onClose }) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [diffStatus, setDiffStatus] = React.useState("OK"); // "OK", "LOW", "HIGH"

  const dispatch = useDispatch();

  const calculateDivergence = React.useCallback(() => {
    if (!contract?.amount || !contract?.previsionAmount) {
      setDiffStatus(null);
      return;
    }

    const THRESHOLD = 10;
    const diff = Math.abs(contract.amount - contract.previsionAmount);

    if (diff === 0) {
      setDiffStatus("OK");
      return;
    }

    if (diff > THRESHOLD) {
      setDiffStatus("HIGH");
      return;
    }

    setDiffStatus("LOW");
  }, [contract]);

  React.useEffect(() => {
    calculateDivergence();
  }, [calculateDivergence]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      await validateContract(contract.id);
      dispatch(showSuccessMessage("app.contract.validated"));
      await loadContract();
    } catch (e) {
      const errorMessage = resolveErrorMessage(e);
      setError(errorMessage);
      dispatch(showErrorMessage(errorMessage));
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const hasDifference = React.useMemo(() => contract?.amount !== contract?.previsionAmount, [contract]);

  return (
    <ActionDialog
      onAccept={onSubmit}
      onClose={onClose}
      open={open}
      maxWidth="xs"
      textConfirm="app.validate"
      textButtonCancel="app.cancel"
      title="app.lccontract.validate"
      disableConfirmButton={loading || error}
    >
      <Typography
        variant="body1"
        paragraph
      >
        <FormattedHTMLMessage id="app.contract.amount.check.reminder" />
      </Typography>

      <InfoItem
        caption={<FormattedMessage id="app.aport.value" />}
        text={
          <Number
            value={contract.amount}
            currency="LCT"
            hideValue={currentUser?.hideValues}
          />
        }
        extraCaption={<FormattedMessage id="app.estimatedAmount" />}
        extraText={
          <Number
            value={contract.previsionAmount}
            currency="LCT"
            hideValue={currentUser?.hideValues}
          />
        }
      />

      {loading && <LoadingSpinner />}

      {hasDifference && (
        <>
          <Divider />
          <br />

          {diffStatus === "LOW" && (
            <>
              <Typography
                color="error"
                paragraph
                variant="body1"
              >
                <FormattedMessage id="app.contract.amount.divergence.low" />
              </Typography>
              <Typography variant="body1">
                <FormattedMessage id="app.contract.amount.divergence.low.check" />
              </Typography>
            </>
          )}
          
          {diffStatus === "HIGH" && (
            <>
              <Typography
                paragraph
                variant="body1"
                style={{ background: "darkred", borderRadius: 4, padding: 4, fontWeight: "bold" }}
              >
                <FormattedMessage id="app.contract.amount.divergence.high" />
              </Typography>
              <Typography
                paragraph
                variant="body1"
              >
                <FormattedMessage id="app.contract.amount.divergence.high.check" />
              </Typography>
            </>
          )}
        </>
      )}
    </ActionDialog>
  );
};

export default DialogContractValidation;
